import React, {
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

import { HiOutlinePaperAirplane } from 'react-icons/hi2'
import { MdOutlineEmail } from 'react-icons/md'
import { PiBookOpenText } from 'react-icons/pi'

import {
  Box,
  Grid,
  Icon,
  Popover,
  PopoverContent,
  Portal,
  VStack,
  useTheme,
} from '@chakra-ui/react'

import { Help } from '@/components/icons'
import { PopoverTrigger } from '@/components/ui'
import { CreateUserFeedbackModal } from '@/features/feedback-crud'
import { BUTTON_PRESS, mixpanel } from '@/utils/analytics'

import { MenuItem } from './MenuItem'
import {
  HELP_POPOVER_STYLES,
  MENU_CONTAINER_STYLES,
  MENU_ITEM_STYLES,
  SUPPORT_LINKS,
} from './constants'

interface HelpPopoverTriggerIProps {
  onClick: (event: MouseEvent | KeyboardEvent) => void
  innerRef: React.Ref<HTMLButtonElement>
}

const HelpPopoverTrigger = ({
  onClick,
  innerRef,
}: HelpPopoverTriggerIProps) => {
  const theme = useTheme()

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(event)
    }
  }

  return (
    <Grid
      _hover={{
        bgColor: 'gray.50',
      }}
      alignItems='center'
      as='button'
      cursor='pointer'
      data-testid='SideBarProfileMenu:help'
      gap={MENU_ITEM_STYLES.GAP}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      p={MENU_ITEM_STYLES.PADDING}
      ref={innerRef as unknown as React.Ref<any>}
      rounded={MENU_ITEM_STYLES.BORDER_RADIUS}
      tabIndex={0}
      templateColumns={`${MENU_ITEM_STYLES.ICON_SIZE} 1fr`}
      transition='ease-in-out'
      transitionDuration={MENU_ITEM_STYLES.TRANSITION_DURATION}
      transitionProperty='all'
      type='button'
      w='100%'
    >
      <Icon
        as={Help}
        boxSize={MENU_ITEM_STYLES.ICON_SIZE}
        strokeColor={theme.colors.sidebar.darkText}
      />
      <Box
        color={theme.colors.sidebar.darkText}
        fontSize={MENU_ITEM_STYLES.TEXT_FONT_SIZE}
        fontWeight='normal'
        lineHeight='shorter'
        textAlign='left'
      >
        Help
      </Box>
    </Grid>
  )
}

interface HelpPopoverIProps {
  isProfilePopoverOpen?: boolean
  onHelpPopoverOpenChange?: (isOpen: boolean) => void
  profilePopoverRef?: RefObject<HTMLDivElement>
  onHelpPopoverRefChange?: (ref: HTMLDivElement | null) => void
}

export const HelpPopover = ({
  isProfilePopoverOpen = false,
  onHelpPopoverOpenChange,
  profilePopoverRef,
  onHelpPopoverRefChange,
}: HelpPopoverIProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const helpPopoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (onHelpPopoverRefChange) {
      onHelpPopoverRefChange(helpPopoverRef.current)
    }
  }, [onHelpPopoverRefChange])

  useEffect(() => {
    if (!isOpen || !profilePopoverRef?.current) return

    const updatePosition = () => {
      const profileRect = profilePopoverRef.current?.getBoundingClientRect()
      if (!profileRect) return

      setPopoverPosition({
        top: profileRect.top,
        left: profileRect.right + HELP_POPOVER_STYLES.HORIZONTAL_OFFSET,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [isOpen, profilePopoverRef])

  // Close HelpPopover when ProfilePopover closes (but not when it just opened)
  useEffect(() => {
    if (!isProfilePopoverOpen && isOpen) {
      setIsOpen(false)
      onHelpPopoverOpenChange?.(false)
    }
  }, [isProfilePopoverOpen, isOpen, onHelpPopoverOpenChange])

  useEffect(() => {
    if (!isOpen) {
      onHelpPopoverOpenChange?.(isFeedbackModalOpen)
    }
  }, [isFeedbackModalOpen, isOpen, onHelpPopoverOpenChange])

  const handleHelpClick = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation()
    event.preventDefault()
    mixpanel.track(`${BUTTON_PRESS} Help from Profile Menu`)

    setIsOpen(true)
    onHelpPopoverOpenChange?.(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    onHelpPopoverOpenChange?.(false)
  }

  return (
    <>
      <Popover
        closeOnBlur={false}
        isOpen={isOpen}
        onClose={handleClose}
        placement='right-start'
        strategy='fixed'
      >
        <PopoverTrigger>
          <HelpPopoverTrigger innerRef={triggerRef} onClick={handleHelpClick} />
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            bg='white'
            borderRadius={HELP_POPOVER_STYLES.BORDER_RADIUS}
            boxShadow='lg'
            data-testid='HelpPopover:content'
            px={MENU_CONTAINER_STYLES.PADDING_X}
            py={HELP_POPOVER_STYLES.PADDING_Y}
            ref={helpPopoverRef}
            sx={{
              position: 'fixed !important',
              top: `${popoverPosition.top}px !important`,
              left: `${popoverPosition.left}px !important`,
              transform: 'none !important',
            }}
            w={HELP_POPOVER_STYLES.WIDTH}
            zIndex={HELP_POPOVER_STYLES.Z_INDEX}
          >
            <VStack align='stretch' spacing={0}>
              <MenuItem
                dataTestId='HelpPopover:sendFeedback'
                icon={HiOutlinePaperAirplane}
                onClick={() => {
                  mixpanel.track(`${BUTTON_PRESS} Send Feedback from Help`)
                  setIsFeedbackModalOpen(true)
                }}
                title='Send Feedback'
              />
              {/* TODO: Release Notes - Not implemented yet */}
              {/* <MenuItem
                dataTestId='HelpPopover:releaseNotes'
                icon={IoDocumentTextOutline}
                onClick={() => {
                  mixpanel.track(`${BUTTON_PRESS} Release Notes from Help`)
                }}
                title='Release Notes'
              /> */}
              <MenuItem
                dataTestId='HelpPopover:emailUs'
                href={SUPPORT_LINKS.EMAIL}
                icon={MdOutlineEmail}
                title='Email Us'
              />
              <MenuItem
                dataTestId='HelpPopover:knowledgeBase'
                href={SUPPORT_LINKS.KNOWLEDGE_BASE}
                icon={PiBookOpenText}
                title='Knowledge Base'
              />
            </VStack>
          </PopoverContent>
        </Portal>
      </Popover>
      {isFeedbackModalOpen && (
        <CreateUserFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => {
            setIsFeedbackModalOpen(false)
          }}
        />
      )}
    </>
  )
}
